// domain 의 요청 함수가 대고 쓰는 HTTP 이음매.
//
// 앱마다 axios 인스턴스가 따로이고 반환 계약도 달라서(student 는 인터셉터가 페이로드를 돌려주고
// teacher 는 응답 객체를 돌려준다) domain 이 어느 한쪽을 직접 import 할 수 없다. 앱이 부트스트랩에서
// 자기 클라이언트를 이 규격에 맞춰 넘긴다. `schema/apiBoundary.ts` 의 configureApiBoundary 와 같은 방식이다.
//
// 공용 래퍼 하나로 통일하는 것은 서버 사용 방식 표준화의 일이다. 그때 앱별 어댑터를 지우고
// 그 자리에 클라이언트를 꽂으면 되고, domain 의 요청 함수는 손대지 않는다.

/**
 * 앱이 module augmentation 으로 `meta` 를 채워 RequestMeta 를 자기 타입으로 좁힌다.
 * domain 은 meta 의 내용을 해석하지 않으므로 여기서는 비워 둔다.
 */
export interface RequestMetaRegistry {}

/**
 * 요청에 딸려 보내는 앱 고유 정보. domain 은 내용을 해석하지 않고 그대로 넘긴다.
 * teacher 는 이것을 트래킹용 쿼리 파라미터(`mp_event_name`)로 바꾼다.
 */
export type RequestMeta = RequestMetaRegistry extends { meta: infer M } ? M : Record<string, string>

export type RequestConfig = {
  params?: unknown
  /** DELETE 가 본문을 실어야 할 때만 쓴다. 나머지 메서드는 인자로 본문을 받는다 */
  data?: unknown
  meta?: RequestMeta
  /** 화면이 앞선 요청을 버려야 할 때(조건이 바뀌어 다시 조회하는 경우) 앱이 넘긴다 */
  signal?: AbortSignal
  /** 토큰 없이 부르는 엔드포인트. 요청 함수가 정한다. 이 요청의 401 은 세션 만료가 아니므로 재발급도 하지 않는다 */
  withoutAuth?: boolean
  /** 엔드포인트가 요구하는 헤더. 헤더 이름은 요청 함수가 알고 값은 호출부가 인자로 준다 */
  headers?: Record<string, string>
  /**
   * 공통 오류 토스트를 막는다. `true` 는 전부, 배열은 그 오류 코드만 막는다.
   * 화면이 실패를 직접 안내할 때 호출부가 넘긴다. 앱마다 막을 수 있는 범위가 달라 어댑터가 자기 방식으로 옮긴다.
   */
  preventErrorToast?: true | string[]
  /** 트래킹 이벤트에 더 싣는 속성. 서버로는 가지 않는다. domain 은 해석하지 않고 넘긴다 */
  trackingProps?: Record<string, unknown>
}

/** 파일 스트림 응답. 파일명은 `Content-Disposition` 에서 읽고 없으면 null 이다 */
export type HttpFile = {
  blob: Blob
  fileName: string | null
}

/**
 * JSON 메서드는 응답 본문(페이로드)으로 resolve 한다. 응답 객체를 벗기는 일은 앱 어댑터가 한다.
 * 파일 스트림은 본문만으로는 파일명을 잃으므로 `getFile` 이 헤더에서 읽은 파일명을 함께 돌려준다.
 */
export type HttpClient = {
  get: <T>(url: string, config?: RequestConfig) => Promise<T>
  post: <T>(url: string, body?: unknown, config?: RequestConfig) => Promise<T>
  put: <T>(url: string, body?: unknown, config?: RequestConfig) => Promise<T>
  patch: <T>(url: string, body?: unknown, config?: RequestConfig) => Promise<T>
  delete: <T>(url: string, config?: RequestConfig) => Promise<T>
  getFile: (url: string, config?: RequestConfig) => Promise<HttpFile>
}

/**
 * `Content-Disposition` 헤더에서 파일명을 꺼낸다. 앱 어댑터가 `getFile` 을 구현할 때 쓴다.
 * RFC 5987 의 `filename*=UTF-8''<퍼센트 인코딩>` 을 `filename=` 보다 먼저 본다. 응답에 따라 두 형태가 다 온다.
 */
export const parseContentDispositionFileName = (header: string | null | undefined) => {
  if (!header) return null
  const encoded = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header)?.[1]
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim().replace(/^"|"$/g, ''))
    } catch {
      // 잘못 인코딩된 값은 아래 filename= 으로 넘어간다
    }
  }
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header)
  const name = (plain?.[2] ?? plain?.[1])?.trim()
  return name ? name : null
}

/**
 * 앱이 부르는 JSON API 서버. 서버마다 베이스 URL·인증·오류 처리가 달라 앱이 클라이언트를 따로 꽂는다.
 * 요청 함수는 자기 엔드포인트가 속한 서버의 이름으로 클라이언트를 고른다.
 * `cms` 는 Strapi(hcms), `messaging` 은 알림을 저장·조회하는 메시징 서버, `search` 는 OpenSearch 색인 검색 서버,
 * `sai` 는 기출 업로드 자료의 지면을 그리고 문항을 찾아 분류하는 AI 서버(mathflat-ai-server)다.
 * `print` 는 학원 PC 의 인쇄 에이전트로 인쇄를 보내는 서버, `ai` 는 보고서 코멘트·AI 튜터의 AI 서버,
 * `homepage` 는 홈페이지 서버, `identify` 는 정산 정보 본인인증 서버, `remoteStorage` 는 사용자별 화면 설정을
 * 키·값으로 저장하는 서버다.
 */
export type HttpClientName =
  | 'main'
  | 'pod'
  | 'cms'
  | 'messaging'
  | 'search'
  | 'sai'
  | 'print'
  | 'ai'
  | 'homepage'
  | 'identify'
  | 'remoteStorage'

const clients: Partial<Record<HttpClientName, HttpClient>> = {}

export const configureHttpClient = (next: HttpClient, name: HttpClientName = 'main') => {
  clients[name] = next
}

export const httpClient = (name: HttpClientName = 'main'): HttpClient => {
  const client = clients[name]
  // 부트스트랩 배선 누락은 그 앱의 모든 요청이 실패한다는 뜻이라 조용히 넘기면 원인이 멀어진다.
  if (!client)
    throw new Error(
      `configureHttpClient(…, '${name}') 가 호출되지 않았다. 앱 부트스트랩에서 설정한다.`,
    )
  return client
}
